import { Button, Grid, makeStyles, TableRow, Tooltip } from '@material-ui/core';
import { ArrowDropDown, EditLocation } from '@material-ui/icons';
import clsx from 'clsx';
import { isNil } from 'lodash';
import { Fragment, memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCustomerMode } from 'src/hooks/useCustomerMode';
import { formatDate } from 'src/modules/date-helpers';

import useRole from '../hooks/useRole';
import { DetailCarDTO, SourceRegisterPotentialDTO, ValuationCountryCode } from '../modules/generated/api';
import { MAX_NUMBER_CHECKABLE } from '../modules/global-vars';
import { countryCodeToOptions, getLabel } from '../modules/labels';
import {
  czSkPairMap,
  getAccountValuationCountry,
  SUPPORTED_COUNTRIES,
  USER_EXTRA_COUNTRIES,
} from '../modules/valuation-country-helpers';
import i18n from '../setup/i18n';
import UserRole from '../types/UserRoles';

import ActionCarCulatorLink from './ActionCarCulatorLink';
import CarDetailTableHeadCell from './CarDetailTableHeadCell';
import CarDetailTableUnitValueCell from './CarDetailTableUnitValueCell';
import { PurchaseGrossFallback } from './PurchaseGrossFallback';
import ReferenceVehicleButton from './ReferenceVehicleButton';
import UnitValue from './UnitValue';
import ValuationCountryIcon from './ValuationCountryIcon';
import ValuationCountrySwitcher from './ValuationCountrySwitcher';

const useStyles = makeStyles((theme) => ({
  printHide: {
    '@media print': {
      display: 'none',
    },
  },
  printShow: {
    '@media screen': {
      display: 'none',
    },
  },
  tableCellEnd: {
    justifyContent: 'flex-end',
  },
  carculatorCell: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  valuationCountryWrapper: {
    float: 'right',
    display: 'flex',
    alignItems: 'center',
  },
  checkBoxRootBtn: {
    fontSize: '0.75rem',
    paddingRight: 0,
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
  highlightGross: {
    backgroundColor: '#F0F0F0',
    '& span': {
      fontWeight: 'bold',
    },
  },
  tooltip: {
    whiteSpace: 'pre-line',
  },
  refCarCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

type CarDetailCarPotentialProps = {
  car: DetailCarDTO;
  comparisonCountries: Set<ValuationCountryCode>;
  setCheckedCountries?: (checkedCountries: Set<ValuationCountryCode>) => void;
  countryPotentials?: {
    [key: string]: SourceRegisterPotentialDTO;
  };
  className?: string;
};

const CarDetailCarPotentials = ({
  car,
  comparisonCountries,
  setCheckedCountries,
  countryPotentials,
  className,
}: CarDetailCarPotentialProps) => {
  const classes = useStyles();
  const { hasRole, objectToken } = useRole();
  const { customerModeActive } = useCustomerMode();
  const { t } = useTranslation();
  const isAdmin = hasRole(UserRole.ADMIN);
  const userId = objectToken?.user_id;
  const isWhitelisted = userId != null && userId in USER_EXTRA_COUNTRIES;
  const ownCountry = getAccountValuationCountry(true);
  const isATValuationCountry = ownCountry === 'AT';
  const showCarCalculatorLink = isATValuationCountry || isAdmin;

  const currentValuationCountry = car.potential?.country;
  const comparingValuationCountriesArray = Array.from(comparisonCountries);

  const isCountryVisible = (country: ValuationCountryCode) =>
    isAdmin ||
    (isWhitelisted && (country === ownCountry || USER_EXTRA_COUNTRIES[userId!].includes(country))) ||
    country === currentValuationCountry ||
    country === ValuationCountryCode.De ||
    (currentValuationCountry !== undefined && country === czSkPairMap[currentValuationCountry]);

  const getIncludedCarsCount = (country: ValuationCountryCode) =>
    car.valuations?.[country as string]?.numberOfCars ?? 0;

  const refCarCountries = comparingValuationCountriesArray.filter(
    (country) => isCountryVisible(country) && getIncludedCarsCount(country) > 0,
  );

  // In customer mode only the access to the reference vehicles remains -- all prices and potentials are dropped
  if (customerModeActive) {
    if (refCarCountries.length === 0) return null;

    return (
      <TableRow className={className}>
        <CarDetailTableHeadCell>{t('referencePopup.refCar_plural')}</CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {refCarCountries.map((country) => (
              <Grid item xs={4} key={`refCars-${country}`} className={classes.refCarCell}>
                <Tooltip
                  title={getLabel(countryCodeToOptions([...SUPPORTED_COUNTRIES], i18n.language), country)}
                  placement="top-end"
                >
                  <div className={classes.valuationCountryWrapper}>
                    <ValuationCountryIcon valuationCountry={country} disableLocationIcon />
                  </div>
                </Tooltip>
                {/* no potential -- the gross purchase price must never reach the dialog header in customer mode */}
                <ReferenceVehicleButton
                  refCar={car}
                  offerId={car.valuations?.[country as string]?.offerId}
                  valuationCountry={country}
                />
              </Grid>
            ))}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow className={className}>
        <CarDetailTableHeadCell>
          {(isAdmin || isWhitelisted) && (
            <ValuationCountrySwitcher
              rootButton={
                <Button size="small" variant="outlined" className={classes.checkBoxRootBtn}>
                  <EditLocation fontSize="small" className={classes.marginRight} />
                  {t('navigation.changeValuationCountry_plural')} (
                  {`${comparisonCountries.size}/${MAX_NUMBER_CHECKABLE}`})
                  <ArrowDropDown fontSize="small" />
                </Button>
              }
              disableValuationTypeSubmenu
              checkboxMode
              checkedCountries={comparisonCountries}
              setCheckedCountries={setCheckedCountries}
              maxNumberCheckable={MAX_NUMBER_CHECKABLE}
            />
          )}
          {!isAdmin && !isWhitelisted && <Trans t={t} i18nKey="navigation.changeValuationCountry" />}
        </CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {comparingValuationCountriesArray.map((country) => (
              <Fragment key={country}>
                {isCountryVisible(country) && (
                  <Grid item xs={4} key={`flag-${country}`}>
                    <Tooltip
                      title={
                        <div className={classes.tooltip}>
                          {`${getLabel(countryCodeToOptions([...SUPPORTED_COUNTRIES], i18n.language), country)}
                          ${t('referencePopup.valuationDate')}: 
                          ${formatDate(car.valuations?.[country]?.valuationDate || '')}`}
                        </div>
                      }
                      placement="top-end"
                    >
                      <div className={classes.valuationCountryWrapper}>
                        <ValuationCountryIcon valuationCountry={country} disableLocationIcon />
                      </div>
                    </Tooltip>
                  </Grid>
                )}
              </Fragment>
            ))}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>
      <TableRow className={clsx(className, classes.highlightGross)}>
        <CarDetailTableHeadCell>
          <Trans t={t} i18nKey="car.prices.purchaseGross_br" />
        </CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {comparingValuationCountriesArray.map((country) => (
              <Fragment key={country}>
                {isCountryVisible(country) && (
                  <Grid item xs={4} key={`purchaseGross-${country}`}>
                    {isNil(countryPotentials?.[country as string]?.purchaseGross?.amount) && <PurchaseGrossFallback />}
                    <UnitValue
                      value={countryPotentials?.[country as string]?.purchaseGross?.amount}
                      unit={countryPotentials?.[country as string]?.purchaseGross?.currency}
                    />
                  </Grid>
                )}
              </Fragment>
            ))}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>
      <TableRow className={className}>
        <CarDetailTableHeadCell>
          {`${t('common.CARculator')} ${t('car.prices.marketPrice')}`} <br />
          {showCarCalculatorLink && (
            <ActionCarCulatorLink firstRegistration={car.firstRegistration} mileage={car.mileage} vin={car.vin} />
          )}
        </CarDetailTableHeadCell>

        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {comparingValuationCountriesArray.map((country) => {
              const valuationPrice = car.valuations?.[country as string]?.price;
              const offerId = car.valuations?.[country as string]?.offerId;
              const numbIncludedCars = car.valuations?.[country as string]?.numberOfCars;
              const potential = car.potentials?.[country];
              return (
                <Fragment key={country}>
                  {isCountryVisible(country) && (
                    <Grid item xs={4} key={`carculator-${country}`} className={classes.carculatorCell}>
                      <div>
                        <UnitValue
                          value={valuationPrice?.mean?.amount}
                          unit={valuationPrice?.mean?.currency}
                          key={`vp-mean-${country}`}
                        />
                        <br />
                        <UnitValue
                          prefix="Min: "
                          value={valuationPrice?.min?.amount}
                          unit={valuationPrice?.min?.currency}
                          key={`vp-min-${country}`}
                        />
                        <br />
                        <UnitValue
                          prefix="Max: "
                          value={valuationPrice?.max?.amount}
                          unit={valuationPrice?.max?.currency}
                          key={`vp-max-${country}`}
                        />
                        <br />
                        {numbIncludedCars && numbIncludedCars > 0 && (
                          <ReferenceVehicleButton
                            refCar={car}
                            potential={potential}
                            offerId={offerId}
                            valuationCountry={country}
                          />
                        )}
                      </div>
                    </Grid>
                  )}
                </Fragment>
              );
            })}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>
      <TableRow className={className}>
        <CarDetailTableHeadCell>{t('car.prices.potentialPriceMin')}</CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {comparingValuationCountriesArray.map((country) => {
              const potentials = countryPotentials?.[country as string];
              return (
                <Fragment key={country}>
                  {isCountryVisible(country) && (
                    <Grid item xs={4} key={`potential-min-${country}`}>
                      <UnitValue
                        value={potentials?.potentialMinAbsolute?.amount}
                        unit={potentials?.potentialMinAbsolute?.currency}
                      />{' '}
                      {potentials?.potentialMinRelative && (
                        <>
                          (<UnitValue value={potentials?.potentialMinRelative} unit="%" />)
                        </>
                      )}
                    </Grid>
                  )}
                </Fragment>
              );
            })}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>
      <TableRow className={className}>
        <CarDetailTableHeadCell>{t('car.prices.potentialPriceMinAverage')}</CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {comparingValuationCountriesArray.map((country) => {
              const potentials = countryPotentials?.[country as string];
              return (
                <Fragment key={country}>
                  {isCountryVisible(country) && (
                    <Grid item xs={4} key={`potential-avg-${country}`}>
                      <UnitValue
                        value={potentials?.potentialMinMeanAbsolute?.amount}
                        unit={potentials?.potentialMinMeanAbsolute?.currency}
                      />{' '}
                      {potentials?.potentialMinMeanRelative && (
                        <>
                          (<UnitValue value={potentials?.potentialMinMeanRelative} unit="%" />)
                        </>
                      )}
                    </Grid>
                  )}
                </Fragment>
              );
            })}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>
    </>
  );
};

export default memo(CarDetailCarPotentials);
